import { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { Redirect } from "wouter";
import {
  Building2, Plus, Calendar, Music, Trophy, Wrench, ChevronRight,
  CheckCircle, Clock, Edit3, ExternalLink, RefreshCw, X, Save,
  Star, Users, Mic, Headphones, Activity,
} from "lucide-react";

const ACTIVITY_CATEGORIES = [
  { value: "quiz_night",   label: "Quiz Night",       emoji: "🧠" },
  { value: "karaoke",      label: "Karaoke",          emoji: "🎤" },
  { value: "live_music",   label: "Live Music",       emoji: "🎸" },
  { value: "open_mic",     label: "Open Mic",         emoji: "🎙️" },
  { value: "comedy",       label: "Comedy Night",     emoji: "😄" },
  { value: "dj",           label: "DJ Night",         emoji: "🎧" },
  { value: "bingo",        label: "Bingo",            emoji: "🎰" },
  { value: "theme_night",  label: "Theme Night",      emoji: "⭐" },
  { value: "happy_hour",   label: "Happy Hour",       emoji: "🍺" },
  { value: "sport",        label: "Sport Event",      emoji: "🏆" },
  { value: "ufc_boxing",   label: "UFC / Boxing",     emoji: "🥊" },
  { value: "pool_table",   label: "Pool Table",       emoji: "🎱" },
  { value: "dart_board",   label: "Dart Board",       emoji: "🎯" },
  { value: "jukebox",      label: "Jukebox",          emoji: "🎵" },
  { value: "sports_tv",    label: "Sports TV",        emoji: "📺" },
  { value: "beer_garden",  label: "Beer Garden",      emoji: "🌿" },
  { value: "food",         label: "Food Served",      emoji: "🍽️" },
  { value: "other",        label: "Other",            emoji: "🍻" },
];

const PERMANENT_CATEGORIES = new Set(["pool_table", "dart_board", "jukebox", "sports_tv", "beer_garden", "food"]);

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

// ── Types ───────────────────────────────────────────────────────────────────

interface MyVenue {
  id: number;
  name: string;
  slug: string | null;
  venueType: string;
  town: string | null;
  address: string | null;
  coverPhoto: string | null;
  isActive: boolean;
  approved: boolean;
  onboardingComplete: boolean;
  onboardingStep: number | null;
}

interface VenueActivity {
  id: number;
  title: string;
  category: string;
  frequency: string;
  dayOfWeek: number | null;
  startTime: string | null;
  priceText: string | null;
  isFree: boolean;
  status: string;
}

interface VenueEvent {
  id: number;
  name: string;
  eventDate: string;
  startTime: string | null;
  status: string;
  approved: boolean;
}

interface SportListing {
  id: number;
  sport: string;
  fixtureName: string | null;
  startDate: string;
  startTime: string | null;
}

interface FacilityItem {
  id: number;
  facilityDefinitionId: number;
  notes: string | null;
  isActive: boolean;
  definition?: { name: string; icon: string; category: string };
}

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function StatusBadge({ isActive, approved }: { isActive: boolean; approved: boolean }) {
  if (isActive && approved) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
        <CheckCircle size={10} /> Live
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 rounded-full">
      <Clock size={10} /> Pending Approval
    </span>
  );
}

// ── Activities tab ───────────────────────────────────────────────────────────

function ActivitiesTab({ venueId }: { venueId: number }) {
  const [items, setItems] = useState<VenueActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: "", category: "quiz_night", frequency: "weekly", dayOfWeek: "1",
    startTime: "20:00", endTime: "", priceText: "", isFree: false,
  });
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    fetch(`/api/venues/${venueId}/activities`, { headers: authHeaders() })
      .then(r => r.json())
      .then((d: { items: VenueActivity[] }) => setItems(d.items ?? []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [venueId]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const catDef = ACTIVITY_CATEGORIES.find(c => c.value === form.category);
      const autoTitle = form.title.trim() || catDef?.label || form.category;
      const isPermanent = form.frequency === "permanent";
      const res = await fetch(`/api/venues/${venueId}/activities`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          title: autoTitle,
          category: form.category,
          frequency: form.frequency,
          dayOfWeek: isPermanent ? null : Number(form.dayOfWeek),
          startTime: isPermanent ? null : (form.startTime || null),
          endTime: isPermanent ? null : (form.endTime || null),
          priceText: form.priceText || null,
          isFree: form.isFree,
        }),
      });
      if (!res.ok) throw new Error("Failed");
      setForm({ title: "", category: "quiz_night", frequency: "weekly", dayOfWeek: "1", startTime: "20:00", endTime: "", priceText: "", isFree: false });
      setShowAdd(false);
      load();
    } catch {
      alert("Failed to add activity. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Remove this activity?")) return;
    setDeletingId(id);
    try {
      await fetch(`/api/venues/${venueId}/activities/${id}`, { method: "DELETE", headers: authHeaders() });
      load();
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) return <div className="py-8 text-center text-slate-400 text-sm">Loading…</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{items.length} activit{items.length === 1 ? "y" : "ies"}</p>
        <button
          onClick={() => setShowAdd(!showAdd)}
          className="flex items-center gap-1.5 text-sm font-medium text-primary bg-primary/10 px-3 py-1.5 rounded-lg hover:bg-primary/20 transition-colors"
        >
          <Plus size={14} /> Add Activity
        </button>
      </div>

      {showAdd && (
        <div className="bg-slate-50 rounded-xl p-4 space-y-3 border border-slate-200">
          <h4 className="font-semibold text-slate-700 text-sm">Add Activity or Offering</h4>

          {/* Category */}
          <div>
            <label className="text-xs text-slate-500 block mb-1">Type of Activity</label>
            <select
              value={form.category}
              onChange={e => {
                const cat = e.target.value;
                const isPerm = PERMANENT_CATEGORIES.has(cat);
                setForm(f => ({
                  ...f,
                  category: cat,
                  frequency: isPerm ? "permanent" : (f.frequency === "permanent" ? "weekly" : f.frequency),
                }));
              }}
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 outline-none focus:border-primary/50 bg-white"
            >
              {ACTIVITY_CATEGORIES.map(c => (
                <option key={c.value} value={c.value}>{c.emoji} {c.label}</option>
              ))}
            </select>
          </div>

          {/* Optional custom title */}
          <div>
            <label className="text-xs text-slate-500 block mb-1">Custom Title <span className="text-slate-400">(optional)</span></label>
            <input
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder={ACTIVITY_CATEGORIES.find(c => c.value === form.category)?.label ?? "Activity name"}
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 outline-none focus:border-primary/50"
            />
          </div>

          {/* Frequency */}
          <div>
            <label className="text-xs text-slate-500 block mb-1">Frequency</label>
            <select
              value={form.frequency}
              onChange={e => setForm(f => ({ ...f, frequency: e.target.value }))}
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 outline-none focus:border-primary/50 bg-white"
            >
              <option value="permanent">Always Available (permanent feature)</option>
              <option value="weekly">Weekly</option>
              <option value="fortnightly">Fortnightly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>

          {form.frequency !== "permanent" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Day</label>
                  <select
                    value={form.dayOfWeek}
                    onChange={e => setForm(f => ({ ...f, dayOfWeek: e.target.value }))}
                    className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 outline-none focus:border-primary/50 bg-white"
                  >
                    {DAY_NAMES.map((d, i) => <option key={i} value={i}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Start Time</label>
                  <input
                    type="time"
                    value={form.startTime}
                    onChange={e => setForm(f => ({ ...f, startTime: e.target.value }))}
                    className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 outline-none focus:border-primary/50"
                  />
                </div>
              </div>
            </>
          )}

          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <label className="text-xs text-slate-500 block mb-1">Price <span className="text-slate-400">(leave blank if free)</span></label>
              <input
                value={form.priceText}
                onChange={e => setForm(f => ({ ...f, priceText: e.target.value }))}
                placeholder="e.g. £5 entry, £2/game"
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 outline-none focus:border-primary/50"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer pb-2">
              <input
                type="checkbox"
                checked={form.isFree}
                onChange={e => setForm(f => ({ ...f, isFree: e.target.checked }))}
                className="accent-primary"
              />
              Free
            </label>
          </div>
          <div className="flex gap-2 pt-1">
            <button
              onClick={() => void handleAdd()}
              disabled={saving}
              className="flex items-center gap-1.5 text-sm font-medium bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
              <Save size={14} /> {saving ? "Saving…" : "Save"}
            </button>
            <button
              onClick={() => setShowAdd(false)}
              className="text-sm text-slate-500 px-3 py-2 rounded-lg hover:bg-slate-100 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div className="py-8 text-center text-slate-400 text-sm bg-slate-50 rounded-xl">
          No activities yet. Add quiz nights, live music, pool tables, and more!
        </div>
      ) : (
        <div className="space-y-2">
          {items.map(item => {
            const catDef = ACTIVITY_CATEGORIES.find(c => c.value === item.category);
            const isPermanent = item.frequency === "permanent";
            return (
              <div key={item.id} className="flex items-center gap-3 bg-white border border-slate-100 rounded-xl px-4 py-3">
                <span className="text-xl flex-shrink-0">{catDef?.emoji ?? "🍻"}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-800 text-sm truncate">{item.title}</div>
                  <div className="text-xs text-slate-400 mt-0.5">
                    {isPermanent ? "Always Available" : (
                      <>
                        {item.frequency === "weekly" ? "Every" : item.frequency === "fortnightly" ? "Every other" : "Monthly on"}{" "}
                        {item.dayOfWeek !== null ? DAY_NAMES[item.dayOfWeek] : ""}
                        {item.startTime ? ` · ${item.startTime.slice(0, 5)}` : ""}
                      </>
                    )}
                    {item.priceText ? ` · ${item.priceText}` : ""}
                    {item.isFree ? " · Free" : ""}
                  </div>
                </div>
                <button
                  onClick={() => void handleDelete(item.id)}
                  disabled={deletingId === item.id}
                  className="text-slate-300 hover:text-red-500 transition-colors disabled:opacity-50"
                >
                  <X size={16} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Events tab ───────────────────────────────────────────────────────────────

function EventsTab({ venueId }: { venueId: number }) {
  const [items, setItems] = useState<VenueEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`/api/events?venueId=${venueId}&status=all&upcoming=false`, { headers: authHeaders() })
      .then(r => r.json())
      .then((d: { items: VenueEvent[] }) => setItems(d.items ?? []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [venueId]);

  if (loading) return <div className="py-8 text-center text-slate-400 text-sm">Loading…</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{items.length} event{items.length !== 1 ? "s" : ""}</p>
        <Link
          href="/submit-event"
          className="flex items-center gap-1.5 text-sm font-medium text-primary bg-primary/10 px-3 py-1.5 rounded-lg hover:bg-primary/20 transition-colors"
        >
          <Plus size={14} /> Submit Event
        </Link>
      </div>

      {items.length === 0 ? (
        <div className="py-8 text-center text-slate-400 text-sm bg-slate-50 rounded-xl">
          No events yet. Submit your first event above.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map(item => (
            <div key={item.id} className="flex items-center gap-3 bg-white border border-slate-100 rounded-xl px-4 py-3">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-800 text-sm truncate">{item.name}</div>
                <div className="text-xs text-slate-400 mt-0.5">
                  {item.eventDate}{item.startTime ? ` at ${item.startTime}` : ""}
                </div>
              </div>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                item.approved ? "bg-emerald-50 text-emerald-700" :
                item.status === "rejected" ? "bg-red-50 text-red-700" :
                "bg-amber-50 dark:bg-amber-900/20 text-amber-700"
              }`}>
                {item.approved ? "Live" : item.status === "rejected" ? "Rejected" : "Pending"}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Sports tab ───────────────────────────────────────────────────────────────

function SportsTab({ venueId }: { venueId: number }) {
  const [items, setItems] = useState<SportListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [form, setForm] = useState({ sport: "football", fixtureName: "", startDate: "", startTime: "" });

  const load = useCallback(() => {
    setLoading(true);
    fetch(`/api/venues/${venueId}/sports`, { headers: authHeaders() })
      .then(r => r.json())
      .then((d: { items: SportListing[] }) => setItems(d.items ?? []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [venueId]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!form.startDate) { alert("Start date is required"); return; }
    setSaving(true);
    try {
      const res = await fetch(`/api/venues/${venueId}/sports`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          sport: form.sport,
          fixtureName: form.fixtureName || null,
          startDate: form.startDate,
          startTime: form.startTime || null,
        }),
      });
      if (!res.ok) throw new Error("Failed");
      setForm({ sport: "football", fixtureName: "", startDate: "", startTime: "" });
      setShowAdd(false);
      load();
    } catch {
      alert("Failed to add sport fixture.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Remove this fixture?")) return;
    setDeletingId(id);
    try {
      await fetch(`/api/venues/${venueId}/sports/${id}`, { method: "DELETE", headers: authHeaders() });
      load();
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) return <div className="py-8 text-center text-slate-400 text-sm">Loading…</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{items.length} fixture{items.length !== 1 ? "s" : ""}</p>
        <button
          onClick={() => setShowAdd(!showAdd)}
          className="flex items-center gap-1.5 text-sm font-medium text-primary bg-primary/10 px-3 py-1.5 rounded-lg hover:bg-primary/20 transition-colors"
        >
          <Plus size={14} /> Add Fixture
        </button>
      </div>

      {showAdd && (
        <div className="bg-slate-50 rounded-xl p-4 space-y-3 border border-slate-200">
          <h4 className="font-semibold text-slate-700 text-sm">New Sport Fixture</h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500 block mb-1">Sport</label>
              <select
                value={form.sport}
                onChange={e => setForm(f => ({ ...f, sport: e.target.value }))}
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 outline-none focus:border-primary/50 bg-white"
              >
                {["football","rugby","golf","tennis","boxing","darts","horse_racing","cricket","other"].map(s => (
                  <option key={s} value={s}>{s.replace("_", " ")}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Fixture Name</label>
              <input
                value={form.fixtureName}
                onChange={e => setForm(f => ({ ...f, fixtureName: e.target.value }))}
                placeholder="e.g. Man Utd vs Arsenal"
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 outline-none focus:border-primary/50"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500 block mb-1">Date *</label>
              <input
                type="date"
                value={form.startDate}
                onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))}
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 outline-none focus:border-primary/50"
              />
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Kick-off Time</label>
              <input
                type="time"
                value={form.startTime}
                onChange={e => setForm(f => ({ ...f, startTime: e.target.value }))}
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 outline-none focus:border-primary/50"
              />
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <button
              onClick={() => void handleAdd()}
              disabled={saving || !form.startDate}
              className="flex items-center gap-1.5 text-sm font-medium bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
              <Save size={14} /> {saving ? "Saving…" : "Save Fixture"}
            </button>
            <button
              onClick={() => setShowAdd(false)}
              className="text-sm text-slate-500 px-3 py-2 rounded-lg hover:bg-slate-100 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div className="py-8 text-center text-slate-400 text-sm bg-slate-50 rounded-xl">
          No sport fixtures yet. Add upcoming games you'll be showing.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map(item => (
            <div key={item.id} className="flex items-center gap-3 bg-white border border-slate-100 rounded-xl px-4 py-3">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-800 text-sm capitalize">
                  {item.sport.replace("_", " ")}{item.fixtureName ? ` — ${item.fixtureName}` : ""}
                </div>
                <div className="text-xs text-slate-400 mt-0.5">
                  {item.startDate}{item.startTime ? ` · ${item.startTime}` : ""}
                </div>
              </div>
              <button
                onClick={() => void handleDelete(item.id)}
                disabled={deletingId === item.id}
                className="text-slate-300 hover:text-red-500 transition-colors disabled:opacity-50"
              >
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Facilities tab ────────────────────────────────────────────────────────────

function FacilitiesTab({ venueId }: { venueId: number }) {
  const [items, setItems] = useState<FacilityItem[]>([]);
  const [defs, setDefs] = useState<{ id: number; name: string; icon: string; category: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<number | null>(null);
  const [removingId, setRemovingId] = useState<number | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      fetch(`/api/venues/${venueId}/facilities`, { headers: authHeaders() }).then(r => r.json()),
      fetch("/api/facility-definitions").then(r => r.json()),
    ])
      .then(([facRes, defRes]: [{ items: FacilityItem[] }, { items: typeof defs }]) => {
        setItems(facRes.items ?? []);
        setDefs(defRes.items ?? []);
      })
      .catch(() => { setItems([]); setDefs([]); })
      .finally(() => setLoading(false));
  }, [venueId]);

  useEffect(() => { load(); }, [load]);

  const activeIds = new Set(items.filter(i => i.isActive).map(i => i.facilityDefinitionId));

  const handleToggle = async (defId: number) => {
    const existing = items.find(i => i.facilityDefinitionId === defId);
    setSaving(defId);
    try {
      if (existing) {
        await fetch(`/api/venues/${venueId}/facilities/${existing.id}`, {
          method: "DELETE",
          headers: authHeaders(),
        });
      } else {
        await fetch(`/api/venues/${venueId}/facilities`, {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({ facilityDefinitionId: defId }),
        });
      }
      load();
    } catch {
      alert("Failed to update facility.");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <div className="py-8 text-center text-slate-400 text-sm">Loading…</div>;

  const byCategory = defs.reduce<Record<string, typeof defs>>((acc, d) => {
    (acc[d.category] ??= []).push(d);
    return acc;
  }, {});

  return (
    <div className="space-y-5">
      {Object.entries(byCategory).map(([category, catDefs]) => (
        <div key={category}>
          <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 capitalize">
            {category.replace("_", " ")}
          </h4>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {catDefs.map(def => {
              const active = activeIds.has(def.id);
              return (
                <button
                  key={def.id}
                  onClick={() => void handleToggle(def.id)}
                  disabled={saving === def.id}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium transition-all border ${
                    active
                      ? "bg-primary text-white border-primary"
                      : "bg-white text-slate-600 border-slate-200 hover:border-primary/30 hover:text-primary"
                  } disabled:opacity-50`}
                >
                  <span className="text-base">{def.icon.length <= 2 ? def.icon : "✓"}</span>
                  <span className="truncate">{def.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Venue manage panel ────────────────────────────────────────────────────────

type VenueTab = "activities" | "events" | "sports" | "facilities";

const VENUE_TABS: { key: VenueTab; label: string; icon: React.ReactNode }[] = [
  { key: "activities", label: "Activities", icon: <Music size={15} /> },
  { key: "events", label: "Events", icon: <Calendar size={15} /> },
  { key: "sports", label: "Sports", icon: <Trophy size={15} /> },
  { key: "facilities", label: "Facilities", icon: <Wrench size={15} /> },
];

function VenuePanel({ venue, onClose }: { venue: MyVenue; onClose: () => void }) {
  const [tab, setTab] = useState<VenueTab>("activities");

  return (
    <div className="mt-4 bg-white rounded-2xl border border-primary/30 shadow-sm overflow-hidden">
      {/* Panel header */}
      <div className="bg-primary/10 px-4 py-3 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-slate-800 truncate">{venue.name}</div>
          <div className="text-xs text-slate-400">{venue.venueType} · {venue.town}</div>
        </div>
        <Link
          href={venue.slug ? `/venues/${venue.slug}` : `/venues/${venue.id}`}
          className="text-primary/70 hover:text-primary transition-colors"
          title="View public page"
        >
          <ExternalLink size={16} />
        </Link>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
          <X size={18} />
        </button>
      </div>

      {/* Tab bar */}
      <div className="flex border-b border-slate-100">
        {VENUE_TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors ${
              tab === t.key
                ? "text-primary border-b-2 border-primary"
                : "text-slate-400 hover:text-slate-600"
            }`}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="p-4">
        {tab === "activities" && <ActivitiesTab venueId={venue.id} />}
        {tab === "events" && <EventsTab venueId={venue.id} />}
        {tab === "sports" && <SportsTab venueId={venue.id} />}
        {tab === "facilities" && <FacilitiesTab venueId={venue.id} />}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function VenueManagement() {
  const { user, isLoading: authLoading } = useAuth();
  const [venues, setVenues] = useState<MyVenue[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeVenueId, setActiveVenueId] = useState<number | null>(null);

  if (!authLoading && !user) return <Redirect to="/login" />;

  const load = useCallback(() => {
    setLoading(true);
    fetch("/api/my-venues", { headers: authHeaders() })
      .then(r => r.json())
      .then((d: { items: MyVenue[] }) => setVenues(d.items ?? []))
      .catch(() => setVenues([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (user) load(); }, [user, load]);

  const activeVenue = venues.find(v => v.id === activeVenueId) ?? null;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-primary px-4 py-6">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
              <Building2 size={22} className="text-white" />
            </div>
            <div className="flex-1">
              <h1 className="text-white font-bold text-xl">My Venues</h1>
              <p className="text-white/70 text-sm">Manage your pub & bar listings</p>
            </div>
            <Link
              href="/list-your-venue"
              className="flex items-center gap-1.5 text-sm font-semibold bg-white text-primary px-3 py-1.5 rounded-xl hover:bg-primary/10 transition-colors"
            >
              <Plus size={16} /> Add Venue
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        {authLoading || loading ? (
          <div className="py-12 text-center text-slate-400 text-sm">Loading your venues…</div>
        ) : venues.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center space-y-4">
            <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto">
              <Building2 size={28} className="text-primary/60" />
            </div>
            <div>
              <h2 className="font-semibold text-slate-700 text-lg">No venues yet</h2>
              <p className="text-sm text-slate-400 mt-1">
                List your pub or bar to appear in What's On and attract more customers.
              </p>
            </div>
            <Link
              href="/list-your-venue"
              className="inline-flex items-center gap-2 bg-primary text-white font-semibold px-5 py-2.5 rounded-xl hover:bg-primary/90 transition-colors text-sm"
            >
              <Plus size={16} /> List Your Venue
            </Link>
          </div>
        ) : (
          <>
            {venues.map(venue => (
              <div key={venue.id}>
                <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                  {/* Venue card */}
                  <div className="flex items-center gap-3 p-4">
                    {venue.coverPhoto ? (
                      <img
                        src={venue.coverPhoto}
                        alt={venue.name}
                        className="w-14 h-14 rounded-xl object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Building2 size={22} className="text-primary/50" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-800 truncate">{venue.name}</span>
                        <StatusBadge isActive={venue.isActive} approved={venue.approved} />
                      </div>
                      <div className="text-xs text-slate-400 mt-0.5 capitalize">
                        {venue.venueType.replace("_", " ")} · {venue.town}
                      </div>
                    </div>
                    <button
                      onClick={() => setActiveVenueId(activeVenueId === venue.id ? null : venue.id)}
                      className={`flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-xl transition-colors ${
                        activeVenueId === venue.id
                          ? "bg-primary text-white"
                          : "bg-primary/10 text-primary hover:bg-primary/20"
                      }`}
                    >
                      <Activity size={14} /> Manage
                    </button>
                  </div>

                  {/* Quick stats */}
                  <div className="grid grid-cols-4 border-t border-slate-50">
                    {VENUE_TABS.map(t => (
                      <button
                        key={t.key}
                        onClick={() => {
                          setActiveVenueId(venue.id);
                        }}
                        className="flex flex-col items-center py-2.5 gap-1 text-slate-400 hover:text-primary transition-colors"
                      >
                        {t.icon}
                        <span className="text-[10px] font-medium">{t.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Expandable manage panel */}
                {activeVenueId === venue.id && (
                  <VenuePanel venue={venue} onClose={() => setActiveVenueId(null)} />
                )}
              </div>
            ))}

            <div className="flex justify-center pt-2">
              <Link
                href="/list-your-venue"
                className="inline-flex items-center gap-2 text-sm font-medium text-primary bg-primary/10 px-4 py-2 rounded-xl hover:bg-primary/20 transition-colors"
              >
                <Plus size={15} /> List another venue
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
